/**
 * Facet Filters Component - Handles collection filtering functionality
 * Manages filter form state, URL updates, and AJAX product grid updates
 */

import { Component } from "./component-base.js";
import { ThemeBus } from "./events-bus.js";
import { EVENTS } from "./event-constants.js";

/**
 * Custom element for collection page filters with AJAX updates
 * @extends HTMLElement
 */

class ActiveFiltersComponent extends HTMLElement {
  #isMobileSelection() {
    return this.hasAttribute('data-mobile-selection');
  }

  #resolveGroupLabel(inputOrLink) {
    if (!inputOrLink) return '';
    const input = inputOrLink.matches?.('input') ? inputOrLink : inputOrLink.querySelector?.('input');
    if (input?.dataset?.groupLabel) return input.dataset.groupLabel.trim();

    const accordion = inputOrLink.closest('filter-accordion, [data-filter-accordion]');
    const accordionLabel = accordion?.querySelector('[data-accordion-trigger] span')?.textContent?.trim();
    if (accordionLabel) return accordionLabel;

    const details = inputOrLink.closest('details');
    const summarySpan = details ? details.querySelector('summary span') : null;
    return (summarySpan?.textContent || '').trim();
  }

  #mobileChipClassName() {
    return 'active-filter-tag mobile-selection-chip inline-flex items-center border border-solid bg-white cursor-pointer';
  }

  #mobileRemoveIconMarkup() {
    return `<span class="mobile-selection-chip__remove" aria-hidden="true"><svg viewBox="0 0 10 10" width="7" height="7" focusable="false"><path d="M2 2L8 8M8 2L2 8" stroke="#fff" stroke-width="1.2" stroke-linecap="round"/></svg></span>`;
  }

  #setFiltersMobile(params) {
    this.innerHTML = '';

    const formatMoneyMajor = (amount) => {
      try {
        const currency = (window.Shopify && Shopify.currency && (Shopify.currency.active || Shopify.currency)) || undefined;
        const nf = new Intl.NumberFormat(undefined, { style: 'currency', currency: currency || 'GBP' });
        return nf.format(Number(amount) || 0);
      } catch (_) {
        return '£' + (Number(amount) || 0).toFixed(2);
      }
    };

    let facetedFilterParam = null;
    let facetedFilterValue = null;
    const facetedFilterOption =
      document.querySelector('#mobile-filter-form [data-faceted-active-filter-option]') ||
      document.querySelector('[data-faceted-active-filter-option]');
    if (facetedFilterOption) {
      facetedFilterParam = facetedFilterOption.getAttribute('data-filter-param');
      facetedFilterValue = facetedFilterOption.getAttribute('data-value');
    }

    const entries = Array.from(params.entries()).filter(([name, value]) => {
      if (!name.startsWith('filter.') || name === 'sort_by' || value === '') return false;
      if (facetedFilterParam && name === facetedFilterParam && value === facetedFilterValue) return false;
      return true;
    });

    const minKey = 'filter.v.price.gte';
    const maxKey = 'filter.v.price.lte';
    const minVal = params.get(minKey);
    const maxVal = params.get(maxKey);
    const hasPrice = (minVal && String(minVal).trim() !== '') || (maxVal && String(maxVal).trim() !== '');

    const drawer = document.querySelector('mobile-filter-drawer-content');
    const facetedLinkSelections = drawer
      ? Array.from(drawer.querySelectorAll('a[data-faceted-filter-link] input[type="checkbox"]:checked'))
      : [];

    if (entries.length === 0 && !hasPrice && facetedLinkSelections.length === 0) {
      this.style.display = 'none';
      const section = this.closest('[data-mobile-selection-section]');
      if (section) section.style.display = 'none';
      return;
    }

    this.style.display = '';
    const section = this.closest('[data-mobile-selection-section]');
    if (section) section.style.display = '';

    facetedLinkSelections.forEach((checkbox) => {
      const link = checkbox.closest('a[data-faceted-filter-link]');
      if (!link) return;

      const tag = document.createElement('button');
      tag.type = 'button';
      tag.className = this.#mobileChipClassName();
      tag.setAttribute('data-faceted-link-chip', 'true');
      tag.setAttribute('data-name', checkbox.getAttribute('name') || '');
      tag.setAttribute('data-value', checkbox.getAttribute('value') || '');

      const valueLabel = (link.querySelector('.filter-option-label')?.textContent || '').trim();
      const groupLabel = this.#resolveGroupLabel(link);
      const label = document.createElement('span');
      if (groupLabel) {
        label.textContent = `${groupLabel}: ${valueLabel}`;
      } else {
        label.textContent = valueLabel || checkbox.value;
      }

      tag.setAttribute('aria-label', `Remove ${label.textContent}`);
      tag.append(label);
      tag.insertAdjacentHTML('beforeend', this.#mobileRemoveIconMarkup());
      this.appendChild(tag);
    });

    if ((minVal && minVal !== '') || (maxVal && maxVal !== '')) {
      const tag = document.createElement('button');
      tag.type = 'button';
      tag.className = this.#mobileChipClassName();

      let text = '';
      if (minVal && maxVal) {
        text = `Price: ${formatMoneyMajor(minVal)} - ${formatMoneyMajor(maxVal)}`;
      } else if (minVal) {
        text = `Price: ${formatMoneyMajor(minVal)} +`;
      } else if (maxVal) {
        text = `Price: Up to ${formatMoneyMajor(maxVal)}`;
      }

      tag.innerHTML = `<span>${text}</span>${this.#mobileRemoveIconMarkup()}`;
      tag.setAttribute('data-filter-name', minKey);
      tag.setAttribute('data-alt-filter-name', maxKey);
      tag.setAttribute('aria-label', 'Remove price filter');
      this.appendChild(tag);
    }

    entries.forEach(([name, value]) => {
      if (name === minKey || name === maxKey) return;

      const tag = document.createElement('button');
      tag.type = 'button';
      tag.className = this.#mobileChipClassName();

      const input = document.querySelector(
        `#mobile-filter-form [name="${CSS.escape(name)}"][value="${CSS.escape(value)}"], [name="${CSS.escape(name)}"][value="${CSS.escape(value)}"]`
      );
      let valueLabel = '';
      if (input?.dataset?.chipLabel) {
        valueLabel = input.dataset.chipLabel;
      } else if (input) {
        valueLabel = (input.closest('label')?.querySelector('.filter-option-label')?.textContent || input.closest('label')?.textContent || '').trim();
        valueLabel = valueLabel.replace(/\(\d+\)\s*$/, '').replace(/\s+/g, ' ').trim();
      }

      const groupLabel = this.#resolveGroupLabel(input || null);
      const label = document.createElement('span');
      if (groupLabel) {
        label.textContent = `${groupLabel}: ${valueLabel || value}`;
      } else {
        label.textContent = valueLabel || value;
      }

      tag.setAttribute('data-name', name);
      tag.setAttribute('data-value', value);
      tag.setAttribute('aria-label', `Remove ${label.textContent}`);
      tag.append(label);
      tag.insertAdjacentHTML('beforeend', this.#mobileRemoveIconMarkup());
      this.appendChild(tag);
    });
  }

  setFilters(params) {
    if (this.#isMobileSelection()) {
      return this.#setFiltersMobile(params);
    }

    // Preserve server-rendered faceted filter tag if it exists
    const facetedFilterTag = this.querySelector('[data-faceted-active-filter]');
    const facetedFilterHTML = facetedFilterTag ? facetedFilterTag.outerHTML : null;
    const facetedFilterHref = facetedFilterTag ? facetedFilterTag.getAttribute('href') : null;
    const hasFacetedFilter = !!facetedFilterTag;

    // Find the faceted filter parameter to exclude it from regular filter tags
    let facetedFilterParam = null;
    let facetedFilterValue = null;
    if (hasFacetedFilter) {
      // Look for the faceted filter option element to get its parameter name and value
      const facetedFilterOption = document.querySelector('[data-faceted-active-filter-option]');
      if (facetedFilterOption) {
        facetedFilterParam = facetedFilterOption.getAttribute('data-filter-param');
        facetedFilterValue = facetedFilterOption.getAttribute('data-value');
      }
    }

    this.innerHTML = '';

    // Helper to format major units (e.g., 500 => £500.00)
    const formatMoneyMajor = (amount) => {
      try {
        const currency = (window.Shopify && Shopify.currency && (window.Shopify.currency.active || Shopify.currency)) || undefined;
        const nf = new Intl.NumberFormat(undefined, { style: 'currency', currency: currency || 'GBP' });
        return nf.format(Number(amount) || 0);
      } catch (_) {
        return '£' + (Number(amount) || 0).toFixed(2);
      }
    };

    // Build entries but skip empty values and the faceted filter parameter
    const entries = Array.from(params.entries()).filter(([name, value]) => {
      if (!name.startsWith('filter.') || name === 'sort_by' || value === '') {
        return false;
      }
      // Exclude the faceted filter parameter if it matches
      if (facetedFilterParam && name === facetedFilterParam && value === facetedFilterValue) {
        return false;
      }
      return true;
    });

    // Price keys participate in visibility
    const minKey = 'filter.v.price.gte';
    const maxKey = 'filter.v.price.lte';
    const minVal = params.get(minKey);
    const maxVal = params.get(maxKey);

    const hasPrice = (minVal && String(minVal).trim() !== '') || (maxVal && String(maxVal).trim() !== '');
    // Don't hide if we have a faceted filter, even if no regular filters
    if (entries.length === 0 && !hasPrice && !hasFacetedFilter) {
      this.style.display = 'none';
      return;
    }

    this.style.display = '';
    if ((minVal && minVal !== '') || (maxVal && maxVal !== '')) {
      const tag = document.createElement('button');
      tag.type = 'button';
      tag.className = this.#mobileChipClassName();

      let text = '';
      if (minVal && maxVal) {
        text = `Price: ${formatMoneyMajor(minVal)} - ${formatMoneyMajor(maxVal)}`;
      } else if (minVal) {
        text = `Price: ${formatMoneyMajor(minVal)} +`;
      } else if (maxVal) {
        text = `Price: Up to ${formatMoneyMajor(maxVal)}`;
      }

      tag.innerHTML = `<span>${text}</span>${this.#mobileRemoveIconMarkup()}`;
      tag.setAttribute('data-filter-name', minKey);
      tag.setAttribute('data-alt-filter-name', maxKey);
      tag.setAttribute('aria-label', 'Remove price filter');

      this.appendChild(tag);
    }

    // Render other non-price filters as individual tags (with group label)
    entries.forEach(([name, value]) => {
      if (name === minKey || name === maxKey) return; // handled above
      const tag = document.createElement('button');
      tag.type = 'button';
      tag.className = this.#mobileChipClassName();

      const input = document.querySelector(`[name="${CSS.escape(name)}"][value="${CSS.escape(value)}"]`);
      let valueLabel = input ? (input.closest('label')?.querySelector('.filter-option-label')?.textContent || input.closest('label')?.textContent || '').trim() : '';
      valueLabel = valueLabel.replace(/\s+/g, ' ').replace(/\(\d+\)\s*$/, '').trim();
      if (input?.dataset?.chipLabel) {
        valueLabel = input.dataset.chipLabel;
      }
      const groupLabel = this.#resolveGroupLabel(input || null);
      const label = document.createElement('span');
      if (groupLabel) {
        label.textContent = `${groupLabel}: ${valueLabel || value}`;
      } else {
        label.textContent = valueLabel || value;
      }

      const finalText = label.textContent;
      tag.setAttribute('data-name', name);
      tag.setAttribute('data-value', value);
      tag.setAttribute('aria-label', `Remove ${finalText}`);
      tag.append(label);
      tag.insertAdjacentHTML('beforeend', this.#mobileRemoveIconMarkup());
      this.appendChild(tag);
    });
    
    // Restore faceted filter tag if it exists and we're still on a faceted collection
    if (hasFacetedFilter && facetedFilterHTML && facetedFilterHref) {
      const tempDiv = document.createElement('div');
      tempDiv.innerHTML = facetedFilterHTML;
      const restoredTag = tempDiv.firstElementChild;
      
      // Update the href to preserve current filter params
      if (restoredTag) {
        // Remove sr-only class and data-seo-rendered attribute when JS takes over
        restoredTag.classList.remove('sr-only');
        restoredTag.removeAttribute('data-seo-rendered');
        restoredTag.classList.add(
          'active-filter-tag',
          'mobile-selection-chip',
          'inline-flex',
          'items-center',
          'border',
          'border-solid',
          'bg-white',
          'cursor-pointer',
          'no-underline',
          'text-inherit'
        );
        
        const url = new URL(facetedFilterHref, window.location.origin);
        const currentParams = new URLSearchParams(window.location.search);
        
        // Preserve non-filter params (sort_by, etc.)
        const sortBy = currentParams.get('sort_by');
        if (sortBy) {
          url.searchParams.set('sort_by', sortBy);
        }
        
        // Preserve other filter params that aren't the faceted filter itself
        for (const [key, value] of currentParams.entries()) {
          if (key.startsWith('filter.') && !url.searchParams.has(key)) {
            url.searchParams.append(key, value);
          }
        }
        
        restoredTag.setAttribute('href', url.pathname + (url.search ? url.search : ''));
        
        // Insert faceted filter tag at the beginning (before regular filters)
        if (this.firstChild) {
          this.insertBefore(restoredTag, this.firstChild);
        } else {
          this.appendChild(restoredTag);
        }
        
        // Bind event listeners after insertion
        // Use a small delay to ensure DOM is ready
        setTimeout(() => {
          const insertedTag = this.querySelector('[data-faceted-active-filter]');
          if (insertedTag) {
            // Handle click on the tag itself
            insertedTag.addEventListener('click', (e) => {
              // If clicking the remove button, handle separately
              if (e.target.closest('.remove-filter-btn')) {
                e.preventDefault();
                e.stopPropagation();
                const href = insertedTag.getAttribute('href');
                if (href) {
                  // Preserve current URL parameters
                  const currentParams = new URLSearchParams(window.location.search);
                  const hrefUrl = new URL(href, window.location.origin);
                  
                  // Preserve sort_by if present
                  const sortBy = currentParams.get('sort_by');
                  if (sortBy) {
                    hrefUrl.searchParams.set('sort_by', sortBy);
                  }
                  
                  // Preserve other non-filter params
                  const q = currentParams.get('q');
                  const type = currentParams.get('type');
                  if (q) hrefUrl.searchParams.set('q', q);
                  if (type) hrefUrl.searchParams.set('type', type);
                  
                  // Preserve any filter params that aren't already in the href
                  for (const [key, value] of currentParams.entries()) {
                    if (key.startsWith('filter.') && !hrefUrl.searchParams.has(key)) {
                      hrefUrl.searchParams.append(key, value);
                    }
                  }
                  
                  window.location.href = hrefUrl.pathname + (hrefUrl.search ? hrefUrl.search : '');
                }
                return;
              }
              // Otherwise, navigate to parent collection (preserve params from href)
              const href = insertedTag.getAttribute('href');
              if (href) {
                // Preserve current URL parameters
                const currentParams = new URLSearchParams(window.location.search);
                const hrefUrl = new URL(href, window.location.origin);
                
                // Preserve sort_by if present
                const sortBy = currentParams.get('sort_by');
                if (sortBy) {
                  hrefUrl.searchParams.set('sort_by', sortBy);
                }
                
                // Preserve other non-filter params
                const q = currentParams.get('q');
                const type = currentParams.get('type');
                if (q) hrefUrl.searchParams.set('q', q);
                if (type) hrefUrl.searchParams.set('type', type);
                
                // Preserve any filter params that aren't already in the href
                for (const [key, value] of currentParams.entries()) {
                  if (key.startsWith('filter.') && !hrefUrl.searchParams.has(key)) {
                    hrefUrl.searchParams.append(key, value);
                  }
                }
                
                window.location.href = hrefUrl.pathname + (hrefUrl.search ? hrefUrl.search : '');
              }
            });
          }
        }, 0);
      }
    }

    // Add a "Clear all" control when there are any active entries
    if (entries.length > 0 || (minVal && minVal !== '') || (maxVal && maxVal !== '') || hasFacetedFilter) {
      const clearAll = document.createElement('button');
      clearAll.type = 'button';
      clearAll.textContent = 'Clear all';
      clearAll.className = 'ml-2 inline-flex items-center text-xs underline text-gray-700 hover:text-black';
      clearAll.addEventListener('click', (e) => {
        e.preventDefault();
        const current = new URLSearchParams(window.location.search);
        // Preserve context params
        const q = current.get('q');
        const type = current.get('type');
        const sortBy = current.get('sort_by');
        
        // If we have a faceted filter, navigate to parent collection without any parameters
        if (hasFacetedFilter && facetedFilterHref) {
          const parentUrl = new URL(facetedFilterHref, window.location.origin);
          // Clear all parameters - navigate to clean parent collection URL
          window.location.href = parentUrl.pathname;
          return;
        }
        
        // Remove all filter.* keys
        Array.from(current.keys()).forEach((key) => {
          if (key.startsWith('filter.')) current.delete(key);
          if (key === 'page') current.delete(key);
        });
        if (q) current.set('q', q);
        if (type) current.set('type', type);
        if (sortBy) current.set('sort_by', sortBy);

        const newUrl = current.toString() ? `${window.location.pathname}?${current}` : window.location.pathname;
        history.replaceState({}, '', newUrl);
        // Let FacetFiltersFormComponent react via popstate
        window.dispatchEvent(new PopStateEvent('popstate'));
      });
      const wrapper = document.createElement('div');
      wrapper.className = 'inline-flex items-center';
      wrapper.appendChild(clearAll);
      this.appendChild(wrapper);
    }
    
  }

  getRemoveButtons() {
    return this.querySelectorAll('.active-filter-tag');
  }

  connectedCallback() {
    // Bind event listeners for server-rendered faceted filter tags
    // This will be called when the element is connected to the DOM
    // The actual binding is handled by FacetFiltersFormComponent.#bindFacetedFilterTags()
  }
}

// Register early
if (!customElements.get('active-filters')) {
  customElements.define('active-filters', ActiveFiltersComponent);
}

class FacetFiltersFormComponent extends HTMLElement {
  connectedCallback() {
    this.form = this.querySelector("form");
    this.activeFiltersEls = Array.from(document.querySelectorAll('active-filters'));

    if (!this.form) {
      // Form may not be parsed yet; watch for it
      const mo = new MutationObserver(() => {
        this.form = this.querySelector('form');
        if (this.form) {
          mo.disconnect();
          this.#initialize();
        }
      });
      mo.observe(this, { childList: true, subtree: true });
      return;
    }
  
    // Form exists, initialize immediately
    this.#initialize();
  }
  
  #initialize() {
    if (this._initialized) return; // Prevent double initialization
    this._initialized = true;
    
    // Check if this is the mobile form (inside mobile-filter-drawer-content)
    // Mobile form should NOT auto-apply filters on change - it waits for Apply button
    this._isMobileForm = !!this.closest('mobile-filter-drawer-content');
    
    // 1. Bind event listeners (mobile previews count; desktop applies on change)
    if (this._isMobileForm) {
      this.#bindMobilePreviewListeners();
    } else {
      this.#bindInitialEventListeners();
    }
    
    // 2. Restore filter UI from URL
    this.#restoreFiltersFromUrl();
    this.#bindDetailsAccordionBehavior();
    this.#bindEmptyStateActions(document.querySelector('#ProductGridContainer'));
    
    // Only bind faceted filter labels/tags for desktop (mobile syncs from desktop)
    if (!this._isMobileForm) {
      this.#bindFacetedFilterLabels();
      this.#bindFacetedFilterTags();
    }
  
    // 3. Render active filters on initial load (only for desktop)
    if (!this._isMobileForm) {
      const params = new URLSearchParams(window.location.search);
      this.#ensureActiveFiltersReady().then((ready) => {
        if (window.__skipActiveFiltersSetOnce) {
          window.__skipActiveFiltersSetOnce = false;
          return;
        }
        if (ready) {
          this.#setActiveFilters(params);
          this.#bindRemoveEvents();
        }
      });
    
      // 4. Don't fetch on initial page load - products are already correct from server render
      // Fetching will only happen when filters actually change via user interaction
    }
  }
  

  #bindRemoveEvents() {
    this.#ensureActiveFiltersReady().then((ready) => {
      if (!ready) return;

      const buttons = Array.from(document.querySelectorAll('active-filters')).flatMap(el => Array.from(el.getRemoveButtons?.() || []));
      buttons.forEach(btn => {
        if (btn.hasAttribute('data-faceted-active-filter')) return;
        btn.addEventListener('click', (e) => {
        e.preventDefault();
        const name = btn.getAttribute('data-name') || btn.getAttribute('data-filter-name');
        const altName = btn.getAttribute('data-alt-filter-name');
        const value = btn.getAttribute('data-value');

        if (altName) {
          document.querySelectorAll(`[name="${CSS.escape(name)}"], [name="${CSS.escape(altName)}"]`).forEach((input) => {
            input.value = '';
          });
          const params = new URLSearchParams(window.location.search);
          params.delete(name);
          params.delete(altName);
          const newUrl = params.toString() ? `${window.location.pathname}?${params}` : window.location.pathname;
          history.replaceState({}, '', newUrl);
          window.dispatchEvent(new PopStateEvent('popstate'));
          return;
        }

        if (!name) return;

        // Try to uncheck the corresponding checkbox in the form first
        let checkbox;
        try {
          const selector = `[name="${CSS.escape(name)}"][value="${CSS.escape(value || '')}"]`;
          checkbox = this.form.querySelector(selector);
        } catch (_) {
          checkbox = null;
        }

        if (checkbox) {
          checkbox.checked = false;
          // Dispatch change event to trigger form's change listener (avoids duplicate calls)
          checkbox.dispatchEvent(new Event('change', { bubbles: true }));
          return;
        }

        // Fallback: manipulate URL params directly (handles cases where input isn't present)
        const params = new URLSearchParams(window.location.search);
        const existing = params.getAll(name);
        if (existing.length > 0) {
          params.delete(name);
          existing.forEach(v => {
            if (value == null || value === '' || v !== value) params.append(name, v);
          });
        }
        // Preserve search context
        const q = params.get('q');
        const type = params.get('type');
        if (q) params.set('q', q);
        if (type) params.set('type', type);

        history.replaceState({}, '', `${window.location.pathname}?${params}`);
        this.#fetchFilteredGrid(params);
        this.#setActiveFilters(params);
        this.#restoreFiltersFromUrl();
        // Update faceted filter links to reflect current filter state
        this.#updateFacetedFilterLinks(params);
        });
      });
    });
  }

  /**
   * Public method to trigger filter change (can be called externally)
   */
  triggerFilterChange() {
    this.#handleFilterChange();
  }

  /**
   * Handles filter form changes by updating URL and fetching new results
   */
  #handleFilterChange() {
    // Exclude empty price fields from FormData by temporarily disabling them
    const tempDisabled = [];
    ['filter.v.price.gte', 'filter.v.price.lte'].forEach((key) => {
      const input = this.form.querySelector(`[name="${key}"]`);
      if (input && (input.value == null || String(input.value).trim() === '')) {
        if (!input.disabled) {
          input.disabled = true;
          tempDisabled.push(input);
        }
      }
    });

    // Exclude faceted filter checkboxes from FormData (they're handled via navigation, not URL params)
    const facetedFilterCheckboxes = [];
    const facetedFilterOption = this.form?.querySelector('[data-faceted-active-filter-option]') || document.querySelector('[data-faceted-active-filter-option]');
    if (facetedFilterOption) {
      const filterParam = facetedFilterOption.getAttribute('data-filter-param');
      const filterValue = facetedFilterOption.getAttribute('data-value');
      if (filterParam && filterValue) {
        // Find all matching checkboxes (mobile and desktop) and temporarily disable them
        const checkboxes = Array.from(document.querySelectorAll(`input[type="checkbox"][name="${CSS.escape(filterParam)}"][value="${CSS.escape(filterValue)}"]`));
        checkboxes.forEach(checkbox => {
          if (!checkbox.disabled) {
            checkbox.disabled = true;
            facetedFilterCheckboxes.push(checkbox);
          }
        });
      }
    }

    const formData = new FormData(this.form);

    // Restore inputs after serialization
    tempDisabled.forEach((el) => { el.disabled = false; });
    facetedFilterCheckboxes.forEach((el) => { el.disabled = false; });
    
    // Collect sorting data - check both mobile (inside form) and desktop (outside form)
    if (!formData.get('sort_by')) {
      // Desktop sort is outside the form, so we need to find it separately
      // Look for any sort select that's NOT the mobile one and NOT inside the mobile drawer
      const allSortSelects = document.querySelectorAll('select[name="sort_by"]');
      const desktopSort = Array.from(allSortSelects).find(select => {
        return select.id !== 'sort-by-mobile' && !select.closest('mobile-filter-drawer-content');
      });
      if (desktopSort && desktopSort.value) {
        formData.set('sort_by', desktopSort.value);
      } else {
        // Check for radio buttons (new popup sort UI)
        const sortRadio = document.querySelector('input[type="radio"][name="sort_by"]:checked');
        if (sortRadio && sortRadio.value) {
          formData.set('sort_by', sortRadio.value);
        }
      }
    }
    
    const params = new URLSearchParams(formData);
    try { ThemeBus.dispatchEvent(new CustomEvent(EVENTS.filtersApplying, { detail: { params: params.toString() } })); } catch (_) {}

    // Preserve search query context (search page) so filters don't drop the query
    const existingParams = new URLSearchParams(window.location.search);
    
    // Check if this is sorting-only (no filter changes)
    const hasFilters = Array.from(params.keys()).some(key => key.startsWith('filter.'));
    const hadFilters = Array.from(existingParams.keys()).some(key => key.startsWith('filter.'));
    const isSortingOnly = !hasFilters && !hadFilters && params.has('sort_by');
    
    // Disable image fade animation for sorting-only changes
    if (isSortingOnly) {
      window.__disableImageFadeOnce = true;
    }
    const q = existingParams.get('q');
    const type = existingParams.get('type');
    if (q) params.set('q', q);
    if (type) params.set('type', type);

    // Normalize price params: drop keys when inputs are blank or invalid
    ['filter.v.price.gte', 'filter.v.price.lte'].forEach((key) => {
      const raw = params.get(key);
      if (raw == null) return;
      const val = String(raw).trim();
      // Drop empty, non-number, or zero values (treat 0 as unset)
      if (val === '' || Number.isNaN(Number(val)) || Number(val) === 0) {
        params.delete(key);
      }
    });
    
    // Check if we need to redirect to parent collection due to multiple filter values
    const redirectResult = this.#checkAndHandleMultipleFilterValues(params);
    if (redirectResult.shouldRedirect) {
      // Redirect to parent collection with faceted filter converted to normal param
      window.location.href = redirectResult.url;
      return; // Stop processing, let the redirect happen
    }
    
    history.replaceState({}, '', `${window.location.pathname}?${params}`);
    try { ThemeBus.dispatchEvent(new CustomEvent(EVENTS.filtersApplied, { detail: { params: params.toString(), status: 'optimistic' } })); } catch (_) {}
    // Update chips immediately, then fetch grid and re-bind remove listeners
    this.#setActiveFilters(params);
    this.#fetchFilteredGrid(params);
    this.#bindRemoveEvents();
    // Update faceted filter links to reflect current filter state
    this.#updateFacetedFilterLinks(params);
    // Re-bind faceted filter tags after DOM updates
    this.#bindFacetedFilterTags();
  }

  /**
   * Gets the dynamic section ID from the shopify-section wrapper element
   * @returns {string} The section ID without the 'section-' prefix
   */
  #getSectionId() {
    // Find the shopify-section element that wraps this filter form
    let sectionElement = this.closest('.shopify-section') || this.form?.closest('.shopify-section');
    
    // If not found, look for the main collection section specifically
    if (!sectionElement) {
      // Look for the main section (contains the product grid)
      sectionElement = document.querySelector('#ProductGridContainer')?.closest('.shopify-section') || 
                     document.querySelector('[id*="__main"]') ||
                     document.querySelector('.shopify-section');
    }
    
    if (sectionElement && sectionElement.id) {
      // Strip the 'section-' prefix from the ID
      // e.g., 'shopify-section-template--26025869312339__main' becomes 'template--26025869312339__main'
      const sectionId = sectionElement.id.replace(/^shopify-section-/, '');
      return sectionId;
    }
    
    // Fallback to 'main' if no section ID found (since collection template uses 'main' section)
    return 'main';
  }

  // Update all active-filters instances with current params
  #setActiveFilters(params) {
    const els = Array.from(document.querySelectorAll('active-filters'));
    els.forEach((el) => {
      try { el.setFilters?.(params); } catch (_) {}
    });
  }

  #gridHasProducts(grid) {
    return Boolean(grid?.querySelector('[data-product-id]'));
  }

  #bindEmptyStateActions(container = document.querySelector('#ProductGridContainer')) {
    if (!container?.querySelectorAll) return;
    container.querySelectorAll('[data-filters-empty-clear]').forEach((btn) => {
      if (btn.dataset.emptyClearBound === 'true') return;
      btn.dataset.emptyClearBound = 'true';
      btn.addEventListener('click', (event) => this.#onEmptyStateClear(event));
    });
  }

  #onEmptyStateClear(event) {
    event.preventDefault();
    const current = new URLSearchParams(window.location.search);
    const q = current.get('q');
    const type = current.get('type');
    const sortBy = current.get('sort_by');
    Array.from(current.keys()).forEach((key) => {
      if (key.startsWith('filter.') || key === 'page') current.delete(key);
    });
    if (q) current.set('q', q);
    if (type) current.set('type', type);
    if (sortBy) current.set('sort_by', sortBy);
    const newUrl = current.toString() ? `${window.location.pathname}?${current}` : window.location.pathname;
    history.replaceState({}, '', newUrl);

    document.querySelectorAll('input[type="checkbox"][name^="filter."]').forEach((checkbox) => {
      checkbox.checked = false;
    });
    document.querySelectorAll('input[name*="filter.v.price"]').forEach((input) => {
      input.value = '';
    });

    this.#restoreFiltersFromUrl();
    this.#setActiveFilters(current);
    this.#fetchFilteredGrid(current);
    this.#bindRemoveEvents();
  }

  #renderEmptyFilterState(currentGrid) {
    const emptyTpl = document.getElementById('FiltersEmptyTemplate');
    currentGrid.innerHTML = emptyTpl ? emptyTpl.innerHTML : '';
    this.#bindEmptyStateActions(currentGrid);
  }

  /**
   * Fetches filtered product grid via AJAX and updates the DOM
   * @param {URLSearchParams} params - The filter parameters
   */
  #fetchFilteredGrid(params) {
    const sectionId = this.#getSectionId();
    const url = `${window.location.pathname}?section_id=${sectionId}&${params}`;
    
    // Prevent duplicate fetches from multiple form instances
    window.__fetchInProgress = true;
    
    // Prevent stale updates by tracking the most recent request
    this._lastFetchToken = (this._lastFetchToken || 0) + 1;
    const currentToken = this._lastFetchToken;

    fetch(url)
      .then((res) => {
        if (!res.ok) throw new Error(`Failed to fetch filtered results: ${res.status} ${res.statusText}`);
        return res.text();
      })
      .then((html) => {
        // Skip if a newer request finished
        if (currentToken !== this._lastFetchToken) return;
        const newDom = new DOMParser().parseFromString(html, 'text/html');
        const newGrid = newDom.querySelector('#ProductGridContainer');
        const currentGrid = document.querySelector('#ProductGridContainer');
        
        if (currentGrid) {
          const incomingHasProducts = this.#gridHasProducts(newGrid);
          const incomingHasEmptyState = Boolean(newGrid?.querySelector('[data-filters-empty-state]'));

          if (newGrid && incomingHasProducts) {
            currentGrid.innerHTML = newGrid.innerHTML;
            
            // Recalculate stagger delays and reset animations
            if (window.ScrollAnimations) {
              if (typeof window.ScrollAnimations.setupStaggerContainers === 'function') {
                window.ScrollAnimations.setupStaggerContainers();
              }
              // Remove animate-in class to retrigger animations
              requestAnimationFrame(() => {
                const newElements = currentGrid.querySelectorAll('[data-animate]');
                newElements.forEach(el => el.classList.remove('animate-in'));
              });
            }
            
            // Initialize lazy loading IMMEDIATELY for new images (no debounce)
            // This prevents flash by observing images before browser tries to render them
            if (window.LazyImageLoader) {
              window.__lazyImageLoaderActive = false; // Reset flag
              new window.LazyImageLoader();
            }
          } else if (newGrid && incomingHasEmptyState) {
            currentGrid.innerHTML = newGrid.innerHTML;
            this.#bindEmptyStateActions(currentGrid);
            try { ThemeBus.dispatchEvent(new CustomEvent(EVENTS.filtersNoResults, { detail: { params: params.toString() } })); } catch (_) {}
          } else {
            this.#renderEmptyFilterState(currentGrid);
            try { ThemeBus.dispatchEvent(new CustomEvent(EVENTS.filtersNoResults, { detail: { params: params.toString() } })); } catch (_) {}
          }

          // Update product count (if present)
          this.#updateProductCount(newDom);
          
          // Update filter options (disabled states and counts) from server response
          this.#updateFilterOptions(newDom, params);

          // Notify other components via ThemeBus (but NOT gridUpdated to avoid duplicate lazy load)
          try { ThemeBus.dispatchEvent(new Event(EVENTS.filtersUpdated)); } catch (_) {}
          try { ThemeBus.dispatchEvent(new CustomEvent(EVENTS.filtersApplied, { detail: { params: params.toString(), status: 'done' } })); } catch (_) {}
        }
        
        // Clear fetch lock
        window.__fetchInProgress = false;
      })
      .catch((error) => {
        console.error('Filter fetch failed:', error);
        // Clear fetch lock on error too
        window.__fetchInProgress = false;
      });
  }

  /**
   * Updates the product count display with the new count from AJAX response
   * @param {Document} newDom - The new DOM from the AJAX response
   */
  #updateProductCount(newDom) {
    const newProductCount = newDom.querySelector('[data-product-count]');
    const currentProductCounts = document.querySelectorAll('[data-product-count]');

    if (newProductCount && currentProductCounts.length > 0) {
      currentProductCounts.forEach((currentProductCount) => {
        currentProductCount.innerHTML = newProductCount.innerHTML;
      });
    }

    this.#updateMobileViewProductsLabel(newDom);
  }

  /**
   * Updates the mobile drawer "View X Products" CTA from fetched HTML or a numeric count
   * @param {Document|number|null} source
   */
  #updateMobileViewProductsLabel(source = null) {
    const applyButton = document.querySelector('[data-mobile-filter-apply]');
    const labelEl = document.querySelector('[data-mobile-view-products-label]');
    if (!applyButton || !labelEl) return;

    let count = null;
    if (typeof source === 'number') {
      count = source;
    } else if (source instanceof Document) {
      const countNode = source.querySelector('[data-product-count]');
      if (countNode) {
        const match = countNode.textContent?.match(/(\d[\d,]*)/);
        if (match) count = parseInt(match[1].replace(/,/g, ''), 10);
      }
      const responseLabel = source.querySelector('[data-mobile-view-products-label]');
      if (responseLabel?.textContent?.trim()) {
        labelEl.textContent = responseLabel.textContent.trim();
        return;
      }
    }

    if (count == null || Number.isNaN(count)) return;

    const one = applyButton.getAttribute('data-view-products-label-one') || 'View 1 Product';
    const otherTemplate = applyButton.getAttribute('data-view-products-label-other') || 'View __COUNT__ Products';
    labelEl.textContent = count === 1 ? one : otherTemplate.replace('__COUNT__', String(count));
  }

  /**
   * Updates filter options (disabled states and counts) from the AJAX response, on DOM updates
   * @param {Document} newDom - The new DOM from the AJAX response
   * @param {URLSearchParams} params - Current filter parameters
   */
  #updateFilterOptions(newDom, params) {
    const newFiltersContainer = newDom.querySelector('#FacetFiltersContainer');
    const currentFiltersContainer = document.querySelector('#FacetFiltersContainer');
    
    if (newFiltersContainer && currentFiltersContainer) {
      // Preserve the open/closed state of accordion elements
      const openAccordions = new Set();
      currentFiltersContainer.querySelectorAll('[data-filter-accordion]').forEach((accordion) => {
        const trigger = accordion.querySelector('[data-accordion-trigger]');
        if (trigger && trigger.getAttribute('aria-expanded') === 'true') {
          const label = trigger.querySelector('span');
          if (label) {
            openAccordions.add(label.textContent.trim());
          }
        }
      });
      
      currentFiltersContainer.innerHTML = newFiltersContainer.innerHTML;
      
      // Restore open state of accordion elements
      currentFiltersContainer.querySelectorAll('[data-filter-accordion]').forEach((accordion) => {
        const trigger = accordion.querySelector('[data-accordion-trigger]');
        const content = accordion.querySelector('[data-accordion-content]');
        const icon = accordion.querySelector('[data-accordion-icon]');
        const label = trigger?.querySelector('span');
        
        if (label && openAccordions.has(label.textContent.trim())) {
          trigger.setAttribute('aria-expanded', 'true');
          content.style.height = 'auto';
          content.classList.remove('overflow-hidden');
          showFilterAccordionPanel(getFilterAccordionPanel(content));
          if (icon) icon.classList.add('rotate-180');
        }
      });
      
      // The new facet-filters-form element will self-initialize via connectedCallback
      queueMicrotask(() => {
        // Restore checked state from URL params for the new form
        const newFacetFiltersForm = currentFiltersContainer.querySelector('facet-filters-form');
        if (newFacetFiltersForm) {
          this.form = newFacetFiltersForm.querySelector('form') || this.form;
        }
        
        this.#restoreFiltersFromUrl();
        
        this.#bindDetailsAccordionBehavior();

        this.#bindFacetedFilterLabels();
        
        // Re-initialize accordions after filter update
        initFilterAccordions();
      });
    }
    
    // Update mobile filters by syncing disabled states and counts from the desktop filters
    this.#syncMobileFiltersFromDesktop(params);
  }

  /**
   * Syncs mobile filter options (disabled states and counts) from the updated desktop filters
   * @param {URLSearchParams} params - Current filter parameters
   */
  #syncMobileFiltersFromDesktop(params) {
    const desktopFiltersContainer = document.querySelector('#FacetFiltersContainer');
    const mobileFiltersContent = document.querySelector('mobile-filter-drawer-content');
    
    if (!desktopFiltersContainer || !mobileFiltersContent) return;
    
    // Build a map of filter states from desktop filters
    // Key: "name|value", Value: { disabled: boolean, count: number }
    const filterStates = new Map();
    
    desktopFiltersContainer.querySelectorAll('input[type="checkbox"]').forEach((input) => {
      const name = input.getAttribute('name');
      const value = input.getAttribute('value');
      if (!name || !value) return;
      
      const key = `${name}|${value}`;
      const isDisabled = input.disabled;
      
      // Try to get the count from the label text
      const label = input.closest('label') || input.closest('a');
      let count = null;
      if (label) {
        const countMatch = label.textContent.match(/\((\d+)\)/);
        if (countMatch) {
          count = parseInt(countMatch[1], 10);
        }
      }
      
      filterStates.set(key, { disabled: isDisabled, count });
    });
    
    // Apply the states to mobile filters
    mobileFiltersContent.querySelectorAll('input[type="checkbox"]').forEach((input) => {
      const name = input.getAttribute('name');
      const value = input.getAttribute('value');
      if (!name || !value) return;
      
      const key = `${name}|${value}`;
      const state = filterStates.get(key);
      
      if (state) {
        // Update disabled state
        const isActive = input.checked;
        input.disabled = state.disabled && !isActive;
        
        // Update the visual styling for disabled state
        const label = input.closest('label');
        const link = input.closest('a');
        const container = label || link;
        
        if (container) {
          if (input.disabled) {
            container.classList.add('opacity-50', 'pointer-events-none');
            container.classList.remove('cursor-pointer');
          } else {
            container.classList.remove('opacity-50', 'pointer-events-none');
            container.classList.add('cursor-pointer');
          }
          
          // Update count text if we have the count
          if (state.count !== null) {
            const countSpan = container.querySelector('.text-gray-500');
            if (countSpan && countSpan.textContent.includes('(')) {
              countSpan.textContent = ` (${state.count})`;
            }
          }
        }
        
        // Update text color for disabled
        const textSpan = container?.querySelector('.text-gray-700, .text-gray-400');
        if (textSpan) {
          if (input.disabled) {
            textSpan.classList.remove('text-gray-700');
            textSpan.classList.add('text-gray-400');
          } else {
            textSpan.classList.remove('text-gray-400');
            textSpan.classList.add('text-gray-700');
          }
        }
      }
    });
    
    // Restore checked state for mobile filters based on URL params
    const mobileForm = mobileFiltersContent.querySelector('form');
    if (mobileForm) {
      // Find faceted filter checkboxes to preserve
      const facetedFilterOption = mobileForm.querySelector('[data-faceted-active-filter-option]');
      let facetedCheckboxes = [];
      if (facetedFilterOption) {
        const filterParam = facetedFilterOption.getAttribute('data-filter-param');
        const filterValue = facetedFilterOption.getAttribute('data-value');
        if (filterParam && filterValue) {
          facetedCheckboxes = Array.from(mobileForm.querySelectorAll(`input[type="checkbox"][name="${CSS.escape(filterParam)}"][value="${CSS.escape(filterValue)}"]`));
        }
      }
      
      // Uncheck all checkboxes first (except faceted)
      mobileForm.querySelectorAll('input[type=checkbox]').forEach((el) => {
        if (!facetedCheckboxes.includes(el)) {
          el.checked = false;
        }
      });
      
      // Check boxes based on URL params
      for (const [name, value] of params.entries()) {
        try {
          const input = mobileForm.querySelector(`[name="${CSS.escape(name)}"][value="${CSS.escape(value)}"]`);
          if (input) input.checked = true;
        } catch (_) {}
        
        if (name === 'sort_by') {
          const sortSelect = mobileForm.querySelector('select[name="sort_by"]');
          if (sortSelect) sortSelect.value = value;
        }
      }
      
      // Ensure faceted checkboxes are checked
      facetedCheckboxes.forEach((cb) => { cb.checked = true; });
    }
  }

  /**
   * Restores filter checkboxes and select elements based on current URL parameters
   */
  #restoreFiltersFromUrl() {
    const params = new URLSearchParams(window.location.search);
    
    // Find faceted filter checkboxes ONLY within this form (not document-wide)
    // This prevents mobile form from finding desktop's faceted filter
    const facetedFilterOption = this.form?.querySelector('[data-faceted-active-filter-option]');
    let facetedCheckboxes = [];
    if (facetedFilterOption) {
      const filterParam = facetedFilterOption.getAttribute('data-filter-param');
      const filterValue = facetedFilterOption.getAttribute('data-value');
      if (filterParam && filterValue) {
        // Find matching checkboxes only within this form
        facetedCheckboxes = Array.from(this.form.querySelectorAll(`input[type="checkbox"][name="${CSS.escape(filterParam)}"][value="${CSS.escape(filterValue)}"]`));
      }
    }
    
    // Uncheck all checkboxes first (except faceted filter checkboxes if they exist)
    this.form.querySelectorAll("input[type=checkbox]").forEach((el) => {
      if (!facetedCheckboxes.includes(el)) {
        el.checked = false;
      }
    });

    for (const [name, value] of params.entries()) {
      // Handle checkboxes
      const input = this.form.querySelector(`[name="${name}"][value="${value}"]`);
      if (input) input.checked = true;
      
      // Handle sort_by select - update all sort selects (both desktop and mobile)
      if (name === 'sort_by') {
        const allSortSelects = document.querySelectorAll('select[name="sort_by"]');
        allSortSelects.forEach(select => {
          if (select) select.value = value;
        });
      }
    }
    
    // Check faceted filter checkboxes explicitly (they're not in URL params, but should be checked)
    // Only check within this form to avoid cross-form contamination
    if (facetedFilterOption) {
      const filterParam = facetedFilterOption.getAttribute('data-filter-param');
      const filterValue = facetedFilterOption.getAttribute('data-value');
      if (filterParam && filterValue) {
        // Find matching checkboxes only within this form
        const formCheckboxes = Array.from(this.form.querySelectorAll(`input[type="checkbox"][name="${CSS.escape(filterParam)}"][value="${CSS.escape(filterValue)}"]`));
        formCheckboxes.forEach((cb) => {
          cb.checked = true;
        });
      }
    }
  }

  /**
   * Bind initial event listeners for filter form, sort select, and history
   */
  #bindInitialEventListeners() {
    if (!this.form) return;
    
    // Bind form change listener
    this._formChangeHandler = () => {
      this.#handleFilterChange();
    };
    this.form.addEventListener("change", this._formChangeHandler);
    
    // Bind popstate listener (only once per instance)
    if (!this._popstateHandler) {
      this._popstateHandler = () => {
        this.#restoreFiltersFromUrl();
        
        // Only fetch if URL has actual filters/sort (not just page param)
        const params = new URLSearchParams(window.location.search);
        const hasFiltersOrSort = Array.from(params.keys()).some(key => 
          key.startsWith('filter.') || key === 'sort_by' || key === 'q' || key === 'type'
        );
        
        // Prevent duplicate fetches from multiple form instances
        // Mark this instance as the one handling the fetch
        if (hasFiltersOrSort && !window.__fetchInProgress) {
          window.__fetchInProgress = true;
          this.#fetchFilteredGrid(params);
        } else {
          // This instance won't fetch, but should still update UI
          this.#setActiveFilters(params);
          this.#bindRemoveEvents();
        }
        // Update faceted filter links to reflect current filter state
        this.#updateFacetedFilterLinks(params);
        // Re-bind faceted filter tags after DOM updates
        this.#bindFacetedFilterTags();
      };
      window.addEventListener("popstate", this._popstateHandler);
    }
  }

  /**
   * Mobile drawer: preview selection chips + product count; apply only on CTA
   */
  #bindMobilePreviewListeners() {
    if (!this.form || this.form.id !== 'mobile-filter-form') return;

    this._formChangeHandler = () => {
      this.#previewMobileFilters();
    };
    this.form.addEventListener('change', this._formChangeHandler);

    this.form.querySelectorAll('input[name*="filter.v.price"]').forEach((input) => {
      input.addEventListener('input', this._formChangeHandler);
    });

    this.#bindMobileFacetedFilterLinks();
    this.#previewMobileFilters();
  }

  #bindMobileFacetedFilterLinks() {
    const drawer = this.closest('mobile-filter-drawer-content');
    if (!drawer) return;

    drawer.querySelectorAll('a[data-faceted-filter-link]').forEach((link) => {
      if (link.dataset.mobileFacetedBound === 'true') return;
      link.dataset.mobileFacetedBound = 'true';

      link.addEventListener('click', (e) => {
        e.preventDefault();

        const linkUrl = link.getAttribute('data-faceted-filter-link') || link.getAttribute('href');
        if (!linkUrl) return;

        const accordion = link.closest('[data-filter-accordion]');
        accordion?.querySelectorAll('a[data-faceted-filter-link] input[type="checkbox"]').forEach((cb) => {
          cb.checked = false;
        });

        const checkbox = link.querySelector('input[type="checkbox"]');
        const isActiveFaceted = link.hasAttribute('data-faceted-active-filter-option');
        if (isActiveFaceted && checkbox?.checked) {
          checkbox.checked = false;
        } else if (checkbox) {
          checkbox.checked = true;
        }

        this.#previewMobileFilters();
      });
    });
  }

  #buildMobilePreviewParams() {
    const tempDisabled = [];
    ['filter.v.price.gte', 'filter.v.price.lte'].forEach((key) => {
      const input = this.form.querySelector(`[name="${key}"]`);
      if (input && (input.value == null || String(input.value).trim() === '')) {
        if (!input.disabled) {
          input.disabled = true;
          tempDisabled.push(input);
        }
      }
    });

    const facetedNavCheckboxes = [];
    this.form.querySelectorAll('a[data-faceted-filter-link] input[type="checkbox"]').forEach((checkbox) => {
      if (!checkbox.disabled) {
        checkbox.disabled = true;
        facetedNavCheckboxes.push(checkbox);
      }
    });

    const facetedFilterOption = this.form.querySelector('[data-faceted-active-filter-option]') || document.querySelector('[data-faceted-active-filter-option]');
    const facetedActiveCheckboxes = [];
    if (facetedFilterOption) {
      const filterParam = facetedFilterOption.getAttribute('data-filter-param');
      const filterValue = facetedFilterOption.getAttribute('data-value');
      if (filterParam && filterValue) {
        this.form.querySelectorAll(`input[type="checkbox"][name="${CSS.escape(filterParam)}"][value="${CSS.escape(filterValue)}"]`).forEach((checkbox) => {
          if (!checkbox.disabled) {
            checkbox.disabled = true;
            facetedActiveCheckboxes.push(checkbox);
          }
        });
      }
    }

    const formData = new FormData(this.form);
    tempDisabled.forEach((el) => { el.disabled = false; });
    facetedNavCheckboxes.forEach((el) => { el.disabled = false; });
    facetedActiveCheckboxes.forEach((el) => { el.disabled = false; });

    const params = new URLSearchParams();
    for (const [name, value] of formData.entries()) {
      if (!name || value === '') continue;
      if (name.startsWith('filter.') || name === 'sort_by') {
        params.append(name, value);
      }
    }

    ['filter.v.price.gte', 'filter.v.price.lte'].forEach((key) => {
      const raw = params.get(key);
      if (raw == null) return;
      const val = String(raw).trim();
      if (val === '' || Number.isNaN(Number(val)) || Number(val) === 0) {
        params.delete(key);
      }
    });

    const current = new URLSearchParams(window.location.search);
    const q = current.get('q');
    const type = current.get('type');
    if (q) params.set('q', q);
    if (type) params.set('type', type);

    return params;
  }

  #resolveMobilePreviewPathname() {
    const drawer = this.closest('mobile-filter-drawer-content');
    if (!drawer) return window.location.pathname;

    const checkedFacetedLink = drawer.querySelector('a[data-faceted-filter-link] input[type="checkbox"]:checked');
    if (checkedFacetedLink) {
      const link = checkedFacetedLink.closest('a[data-faceted-filter-link]');
      const linkUrl = link?.getAttribute('data-faceted-filter-link') || link?.getAttribute('href');
      if (linkUrl) {
        try {
          return new URL(linkUrl, window.location.origin).pathname;
        } catch (_) {}
      }
    }

    return window.location.pathname;
  }

  #previewMobileFilters() {
    if (!this.form || this.form.id !== 'mobile-filter-form') return;

    const params = this.#buildMobilePreviewParams();
    const mobileSelection = document.querySelector('active-filters[data-mobile-selection]');
    if (mobileSelection?.setFilters) {
      mobileSelection.setFilters(params);
      this.#bindMobileSelectionRemoveEvents(mobileSelection);
    }

    this.#previewMobileProductCount(params);
  }

  #bindMobileSelectionRemoveEvents(selectionEl) {
    selectionEl.querySelectorAll('.active-filter-tag').forEach((btn) => {
      btn.addEventListener('click', (e) => {
        e.preventDefault();
        e.stopPropagation();

        if (btn.hasAttribute('data-faceted-link-chip')) {
          const name = btn.getAttribute('data-name');
          const value = btn.getAttribute('data-value');
          if (name && value) {
            this.form.querySelectorAll(`[name="${CSS.escape(name)}"][value="${CSS.escape(value)}"]`).forEach((input) => {
              input.checked = false;
            });
          }
          this.#previewMobileFilters();
          return;
        }

        const name = btn.getAttribute('data-name') || btn.getAttribute('data-filter-name');
        const altName = btn.getAttribute('data-alt-filter-name');
        const value = btn.getAttribute('data-value');
        if (!name) return;

        if (altName) {
          this.form.querySelectorAll(`[name="${CSS.escape(name)}"], [name="${CSS.escape(altName)}"]`).forEach((input) => {
            input.value = '';
          });
        } else {
          let checkbox;
          try {
            checkbox = this.form.querySelector(`[name="${CSS.escape(name)}"][value="${CSS.escape(value || '')}"]`);
          } catch (_) {
            checkbox = null;
          }
          if (checkbox) {
            checkbox.checked = false;
            const visual = checkbox.closest('label')?.querySelector('.filter-tickbox, span.h-4.w-4');
            if (visual) {
              visual.classList.remove('bg-input-text', 'border-input-text', 'bg-primary');
              if (!visual.classList.contains('filter-tickbox')) {
                visual.classList.add('bg-white', 'border-primary');
              }
              const checkmark = visual.querySelector('.filter-tickbox__check, svg');
              if (checkmark) {
                checkmark.classList.remove('opacity-100');
                checkmark.classList.add('opacity-0');
                checkmark.style.opacity = '0';
              }
            }
          }
        }

        this.#previewMobileFilters();
      }, { once: true });
    });
  }

  #previewMobileProductCount(params) {
    const sectionId = this.#getSectionId();
    const pathname = this.#resolveMobilePreviewPathname();
    const url = `${pathname}?section_id=${sectionId}&${params}`;

    this._mobilePreviewToken = (this._mobilePreviewToken || 0) + 1;
    const token = this._mobilePreviewToken;

    clearTimeout(this._mobilePreviewTimer);
    this._mobilePreviewTimer = setTimeout(() => {
      fetch(url)
        .then((res) => {
          if (!res.ok) throw new Error(`Failed to preview filters: ${res.status}`);
          return res.text();
        })
        .then((html) => {
          if (token !== this._mobilePreviewToken) return;
          const newDom = new DOMParser().parseFromString(html, 'text/html');
          this.#updateMobileViewProductsLabel(newDom);
        })
        .catch(() => {});
    }, 250);
  }

  // Close sibling filter popovers when one opens (horizontal UI only, not mobile drawer)
  #bindDetailsAccordionBehavior() {
    if (!this.form) return;
    
    // Skip accordion behavior for mobile filter drawer - allow multiple accordions open
    if (this._isMobileForm) {
      return;
    }
    
    const container = this.form.querySelector('filter-set') || this.form;
    const detailsList = container.querySelectorAll('details');
    if (!detailsList.length) return;
    detailsList.forEach((el) => {
      el.addEventListener('toggle', () => {
        if (!el.open) return;
        detailsList.forEach((other) => {
          if (other !== el) other.open = false;
        });
      });
    });
  }

  /**
   * Checks if multiple values of the same filter are active and we're on a faceted collection.
   * If so, returns redirect info to parent collection with faceted filter as normal param.
   * @param {URLSearchParams} params - Current filter parameters
   * @returns {Object} { shouldRedirect: boolean, url: string }
   */
  #checkAndHandleMultipleFilterValues(params) {
    // Check if we're on a faceted collection
    const facetedFilterOption = this.form?.querySelector('[data-faceted-active-filter-option]') || document.querySelector('[data-faceted-active-filter-option]');
    if (!facetedFilterOption) {
      return { shouldRedirect: false, url: '' };
    }
    
    // Get the faceted filter info
    const facetedFilterParam = facetedFilterOption.getAttribute('data-filter-param');
    const facetedFilterValue = facetedFilterOption.getAttribute('data-value');
    if (!facetedFilterParam || !facetedFilterValue) {
      return { shouldRedirect: false, url: '' };
    }
    
    // Check if any filter param has 2+ values
    const filterParamCounts = new Map();
    for (const [key, value] of params.entries()) {
      if (key.startsWith('filter.')) {
        const count = filterParamCounts.get(key) || 0;
        filterParamCounts.set(key, count + 1);
      }
    }
    
    // Check if any filter has 2+ values
    let hasMultipleValues = false;
    for (const count of filterParamCounts.values()) {
      if (count >= 2) {
        hasMultipleValues = true;
        break;
      }
    }
    
    if (!hasMultipleValues) {
      return { shouldRedirect: false, url: '' };
    }
    
    // Get the parent collection URL from the active faceted filter link
    const facetedFilterLink = facetedFilterOption.closest('a[data-faceted-filter-link]') || 
                              document.querySelector('a[data-faceted-active-filter-option]');
    if (!facetedFilterLink) {
      return { shouldRedirect: false, url: '' };
    }
    
    const parentCollectionUrl = facetedFilterLink.getAttribute('href') || 
                                facetedFilterLink.getAttribute('data-faceted-filter-link');
    if (!parentCollectionUrl) {
      return { shouldRedirect: false, url: '' };
    }
    
    try {
      // Build new URL with parent collection path and all current params + faceted filter as normal param
      const parentUrlObj = new URL(parentCollectionUrl, window.location.origin);
      const newUrl = new URL(parentUrlObj.pathname, window.location.origin);
      
      // Copy all current params
      for (const [key, value] of params.entries()) {
        newUrl.searchParams.delete(key); // Remove existing to avoid duplicates
        params.getAll(key).forEach(v => {
          newUrl.searchParams.append(key, v);
        });
      }
      
      // Add the faceted filter as a normal URL parameter
      newUrl.searchParams.append(facetedFilterParam, facetedFilterValue);
      
      const finalUrl = newUrl.pathname + (newUrl.search ? newUrl.search : '');
      return { shouldRedirect: true, url: finalUrl };
    } catch (e) {
      console.warn('Failed to build redirect URL:', e);
      return { shouldRedirect: false, url: '' };
    }
  }

  /**
   * Updates faceted filter links' href and data-faceted-filter-link attributes
   * to reflect the current filter state after AJAX updates
   * For active faceted filters: keeps current params but changes base URL to parent collection
   * @param {URLSearchParams} currentParams - Current URL parameters
   */
  #updateFacetedFilterLinks(currentParams) {
    // Find all faceted filter links
    const facetedLinks = document.querySelectorAll('a[data-faceted-filter-link]');
    
    facetedLinks.forEach((link) => {
      const currentLinkUrl = link.getAttribute('data-faceted-filter-link') || link.getAttribute('href');
      if (!currentLinkUrl) return;
      
      try {
        const linkUrlObj = new URL(currentLinkUrl, window.location.origin);
        
        // Check if this is an active faceted filter (navigates to parent)
        const isActiveFaceted = link.hasAttribute('data-faceted-active-filter-option');
        let filterParam = link.getAttribute('data-filter-param');
        const filterValue = link.getAttribute('data-value');
        
        // For regular faceted links, get the param from the checkbox inside
        if (!filterParam) {
          const checkbox = link.querySelector('input[type="checkbox"]');
          if (checkbox) {
            filterParam = checkbox.getAttribute('name');
          }
        }
        
        if (isActiveFaceted) {
          // For active faceted filters: keep current params, change base URL to parent collection
          const parentCollectionPath = linkUrlObj.pathname; // This is the parent collection URL from the original link
          const newUrl = new URL(parentCollectionPath, window.location.origin);
          
          // Copy all current URL params, excluding the faceted filter param this link represents
          for (const [key, value] of currentParams.entries()) {
            if (filterParam && key === filterParam) {
              // Exclude this specific faceted filter value, but keep other values for the same param
              const paramValues = currentParams.getAll(key);
              paramValues.forEach(v => {
                if (v !== filterValue) {
                  newUrl.searchParams.append(key, v);
                }
              });
            } else {
              // Preserve all other params (sort_by, q, type, and other filter params)
              newUrl.searchParams.delete(key); // Remove existing to avoid duplicates
              currentParams.getAll(key).forEach(v => {
                newUrl.searchParams.append(key, v);
              });
            }
          }
          
          // Update both href and data attribute
          const newUrlString = newUrl.pathname + (newUrl.search ? newUrl.search : '');
          link.setAttribute('href', newUrlString);
          link.setAttribute('data-faceted-filter-link', newUrlString);
        } else {
          // For regular faceted links: keep the target collection URL, but update params with current filters
          const targetCollectionPath = linkUrlObj.pathname; // This is the target faceted collection URL
          const newUrl = new URL(targetCollectionPath, window.location.origin);
          
          // Copy all current URL params, excluding the faceted filter param this link represents
          for (const [key, value] of currentParams.entries()) {
            if (filterParam && key === filterParam) {
              // Exclude the faceted filter param (the collection URL itself represents this filter)
              continue;
            } else {
              // Preserve all other params
              newUrl.searchParams.delete(key); // Remove existing to avoid duplicates
              currentParams.getAll(key).forEach(v => {
                newUrl.searchParams.append(key, v);
              });
            }
          }
          
          // Update both href and data attribute
          const newUrlString = newUrl.pathname + (newUrl.search ? newUrl.search : '');
          link.setAttribute('href', newUrlString);
          link.setAttribute('data-faceted-filter-link', newUrlString);
        }
      } catch (e) {
        console.warn('Failed to update faceted filter link:', e);
      }
    });
  }

  /**
   * Binds click events on faceted filter links to navigate with custom logic
   * The links are visible <a> tags for SEO, but we intercept clicks for custom behavior
   */
  #bindFacetedFilterLabels() {
    // Find all faceted filter links (both in form and document-wide)
    const facetedLinks = document.querySelectorAll('a[data-faceted-filter-link]:not([data-faceted-bound])');
    facetedLinks.forEach((link) => {
      // Mark as bound to prevent duplicate listeners
      link.setAttribute('data-faceted-bound', 'true');
      
      // Add click handler (intercept default navigation for custom logic)
      link.addEventListener('click', (e) => {
        e.preventDefault();
        let linkUrl = link.getAttribute('data-faceted-filter-link');
        if (linkUrl) {
          // Preserve current URL parameters (sort_by, etc.) when navigating to faceted collection
          const currentParams = new URLSearchParams(window.location.search);
          const linkUrlObj = new URL(linkUrl, window.location.origin);
          
          // Preserve sort_by if present
          const sortBy = currentParams.get('sort_by');
          if (sortBy) {
            linkUrlObj.searchParams.set('sort_by', sortBy);
          }
          
          // Preserve other non-filter params (q, type, etc.)
          const q = currentParams.get('q');
          const type = currentParams.get('type');
          if (q) linkUrlObj.searchParams.set('q', q);
          if (type) linkUrlObj.searchParams.set('type', type);
          
          // Preserve any filter params that aren't already in the link
          for (const [key, value] of currentParams.entries()) {
            if (key.startsWith('filter.') && !linkUrlObj.searchParams.has(key)) {
              linkUrlObj.searchParams.append(key, value);
            }
          }
          
          window.location.href = linkUrlObj.pathname + (linkUrlObj.search ? linkUrlObj.search : '');
        }
      });
    });
  }
  
  /**
   * Binds click events on faceted filter tags (active filter tags)
   */
  #bindFacetedFilterTags() {
    // Find all faceted filter tags (server-rendered or dynamically created)
    const facetedTags = document.querySelectorAll('[data-faceted-active-filter]');
    facetedTags.forEach((tag) => {
      // Remove existing listeners to avoid duplicates
      const newTag = tag.cloneNode(true);
      tag.parentNode.replaceChild(newTag, tag);
      
      // Handle click on the tag itself (navigate to parent)
      newTag.addEventListener('click', (e) => {
        // If clicking the remove button, handle separately
        if (e.target.closest('.remove-filter-btn')) {
          e.preventDefault();
          e.stopPropagation();
          const href = newTag.getAttribute('href');
          if (href) {
            window.location.href = href;
          }
          return;
        }
        // Otherwise, navigate to parent collection (preserve params from href)
        const href = newTag.getAttribute('href');
        if (href) {
          // Preserve current URL parameters
          const currentParams = new URLSearchParams(window.location.search);
          const hrefUrl = new URL(href, window.location.origin);
          
          // Preserve sort_by if present
          const sortBy = currentParams.get('sort_by');
          if (sortBy) {
            hrefUrl.searchParams.set('sort_by', sortBy);
          }
          
          // Preserve other non-filter params
          const q = currentParams.get('q');
          const type = currentParams.get('type');
          if (q) hrefUrl.searchParams.set('q', q);
          if (type) hrefUrl.searchParams.set('type', type);
          
          // Preserve any filter params that aren't already in the href
          for (const [key, value] of currentParams.entries()) {
            if (key.startsWith('filter.') && !hrefUrl.searchParams.has(key)) {
              hrefUrl.searchParams.append(key, value);
            }
          }
          
          window.location.href = hrefUrl.pathname + (hrefUrl.search ? hrefUrl.search : '');
        }
      });
    });
  }

  /**
   * Ensures the <active-filters> custom element is present and upgraded
   * @returns {Promise<boolean>} true if ready
   */
  async #ensureActiveFiltersReady() {
    if (!this.activeFiltersEl) {
      this.activeFiltersEl = document.querySelector('active-filters');
    }
    if (!this.activeFiltersEl) return false;
    // Try immediate upgrade if the definition exists
    try {
      if (customElements.get('active-filters')) {
        customElements.upgrade?.(this.activeFiltersEl);
      }
    } catch (_) {}
    if (typeof this.activeFiltersEl.setFilters === 'function') return true;
    // Wait a microtask and a frame to allow upgrade after DOM replacement
    await Promise.resolve();
    await new Promise((r) => requestAnimationFrame(r));
    try {
      if (customElements.get('active-filters')) {
        customElements.upgrade?.(this.activeFiltersEl);
      } else {
        await customElements.whenDefined('active-filters');
        customElements.upgrade?.(this.activeFiltersEl);
      }
    } catch (_) {}
    // Re-select in case the element reference changed after swap
    this.activeFiltersEl = document.querySelector('active-filters');
    return !!(this.activeFiltersEl && typeof this.activeFiltersEl.setFilters === 'function');
  }

}

export async function initFilters() {
  if (!customElements.get("facet-filters-form")) {
    customElements.define("facet-filters-form", FacetFiltersFormComponent);

    // Attach a single global listener for sort changes (outside the form)
    // This prevents multiple listeners from firing when there are multiple facet-filters-form elements
    document.addEventListener("change", (event) => {
      // Handle sort radio buttons
      if (event.target.matches && (event.target.matches('select[name="sort_by"]') || event.target.matches('input[name="sort_by"]'))) {
        // Check if this is mobile or desktop
        const isMobile = event.target.closest('mobile-filter-drawer-content');
        
        if (!isMobile) {
          // Desktop sort - check if it's inside a form or standalone
          const sortForm = event.target.closest('form');
          
          if (sortForm) {
            // If inside a form, trigger the form's filter change
            const formElement = sortForm.closest('facet-filters-form');
            if (formElement && formElement.triggerFilterChange) {
              formElement.triggerFilterChange();
            }
          } else {
            // Standalone desktop sort (not in a form) - manually update URL
            const sortValue = event.target.value;
            const params = new URLSearchParams(window.location.search);
            params.set('sort_by', sortValue);
            const newUrl = `${window.location.pathname}?${params.toString()}`;
            
            // Update URL and trigger page update
            history.replaceState({}, '', newUrl);
            window.dispatchEvent(new PopStateEvent('popstate'));
          }
        }
        
        // Update the sort label text and visual checkbox state (for both desktop and mobile)
        if (event.target.type === 'radio' && event.target.checked) {
          const label = event.target.closest('label');
          if (label) {
            // Update dropdown summary text (desktop only - has details element)
            const sortText = label.querySelector('span.text-primary')?.textContent;
            const summary = event.target.closest('details')?.querySelector('summary > span');
            if (summary && sortText) {
              summary.textContent = sortText;
            }
            
            // Update visual checkbox states (fake checkboxes) - works for both details and filter-accordion
            const sortContainer = event.target.closest('details, filter-accordion');
            if (sortContainer) {
              // Remove checked state from all sort options
              sortContainer.querySelectorAll('input[name="sort_by"]').forEach(radio => {
                const visualCheckbox = radio.nextElementSibling;
                if (visualCheckbox && visualCheckbox.tagName === 'SPAN') {
                  visualCheckbox.classList.remove('bg-input-text', 'border-input-text');
                  visualCheckbox.classList.add('bg-white', 'border-input-border');
                  const svg = visualCheckbox.querySelector('svg');
                  if (svg) svg.classList.add('opacity-0');
                  if (svg) svg.classList.remove('opacity-100');
                }
              });
              
              // Add checked state to selected option
              const visualCheckbox = event.target.nextElementSibling;
              if (visualCheckbox && visualCheckbox.tagName === 'SPAN') {
                visualCheckbox.classList.add('bg-input-text', 'border-input-text');
                visualCheckbox.classList.remove('bg-white', 'border-input-border');
                const svg = visualCheckbox.querySelector('svg');
                if (svg) svg.classList.remove('opacity-0');
                if (svg) svg.classList.add('opacity-100');
              }
            }
          }
        }
      }
      
      // Handle filter checkboxes (update visual fake checkbox state)
      if (event.target.matches && event.target.matches('input[type="checkbox"]') && event.target.classList.contains('sr-only')) {
        const visualCheckbox = event.target.nextElementSibling;
        if (visualCheckbox && visualCheckbox.tagName === 'SPAN' && visualCheckbox.querySelector('svg')) {
          // This is a fake checkbox - update its visual state
          if (event.target.checked) {
            visualCheckbox.classList.add('bg-input-text', 'border-input-text');
            visualCheckbox.classList.remove('bg-white', 'border-input-border');
            const svg = visualCheckbox.querySelector('svg');
            if (svg) {
              svg.classList.remove('opacity-0');
              svg.classList.add('opacity-100');
            }
          } else {
            visualCheckbox.classList.remove('bg-input-text', 'border-input-text');
            visualCheckbox.classList.add('bg-white', 'border-input-border');
            const svg = visualCheckbox.querySelector('svg');
            if (svg) {
              svg.classList.add('opacity-0');
              svg.classList.remove('opacity-100');
            }
          }
        }
      }
    });
  }
  
  // Initialize filter accordions
  initFilterAccordions();
  initMobileFilterOptionsDisplay();
}

function applyMobileFilterOptionsList(list, { limit, seeMoreLabel, seeLessLabel, expanded = false }) {
  list.querySelector('[data-filter-options-toggle]')?.remove();

  const items = list.querySelectorAll(':scope > [data-filter-option]');
  let hiddenCount = 0;

  items.forEach((item, index) => {
    if (!expanded && index >= limit) {
      item.classList.add('hidden');
      item.setAttribute('data-filter-option-hidden', '');
      hiddenCount++;
    } else {
      item.classList.remove('hidden');
      item.removeAttribute('data-filter-option-hidden');
    }
  });

  if (items.length <= limit) return;

  const button = document.createElement('button');
  button.type = 'button';
  button.className = 'text-xs text-inherit opacity-70 hover:underline w-fit pb-5';
  button.dataset.filterOptionsToggle = '';
  button.textContent = expanded ? seeLessLabel : `${seeMoreLabel} (+${hiddenCount})`;
  button.addEventListener('click', (event) => {
    event.preventDefault();
    applyMobileFilterOptionsList(list, {
      limit,
      seeMoreLabel,
      seeLessLabel,
      expanded: !expanded,
    });
  });
  list.appendChild(button);
}

function initMobileFilterOptionsDisplay() {
  const configEl = document.querySelector('[data-mobile-filter-config]');
  if (!configEl) return;

  const display = configEl.dataset.optionsDisplay || 'scroll';
  const limit = parseInt(configEl.dataset.optionsLimit || '8', 10);
  const seeMoreLabel = configEl.dataset.seeMoreLabel || 'See more';
  const seeLessLabel = configEl.dataset.seeLessLabel || 'See less';
  const drawer = document.querySelector('mobile-filter-drawer-content');
  if (!drawer) return;

  drawer.querySelectorAll('[data-filter-options-list]').forEach((list) => {
    const accordionContent = list.closest('[data-accordion-content]');
    const items = list.querySelectorAll(':scope > [data-filter-option]');

    if (display === 'scroll') {
      list.querySelector('[data-filter-options-toggle]')?.remove();
      accordionContent?.classList.add('max-h-80', 'overflow-y-scroll');
      items.forEach((item) => {
        item.classList.remove('hidden');
        item.removeAttribute('data-filter-option-hidden');
      });
      return;
    }

    accordionContent?.classList.remove('max-h-80', 'overflow-y-scroll');
    applyMobileFilterOptionsList(list, { limit, seeMoreLabel, seeLessLabel, expanded: false });
  });
}

const FILTER_ACCORDION_PANEL_HIDDEN = ['opacity-0', '-translate-x-2'];
const FILTER_ACCORDION_PANEL_VISIBLE = ['opacity-100', 'translate-x-0'];

function getFilterAccordionPanel(content) {
  return content.querySelector('[data-filter-accordion-panel]') || content.firstElementChild;
}

function hideFilterAccordionPanel(panel) {
  if (!panel) return;
  panel.classList.remove(...FILTER_ACCORDION_PANEL_VISIBLE);
  panel.classList.add(...FILTER_ACCORDION_PANEL_HIDDEN);
}

function showFilterAccordionPanel(panel) {
  if (!panel) return;
  panel.classList.remove(...FILTER_ACCORDION_PANEL_HIDDEN);
  panel.classList.add(...FILTER_ACCORDION_PANEL_VISIBLE);
}

// Initialize filter accordions with button-based behavior
function initFilterAccordions() {
  const accordions = document.querySelectorAll('[data-filter-accordion]');
  
  accordions.forEach(accordion => {
    const trigger = accordion.querySelector('[data-accordion-trigger]');
    const content = accordion.querySelector('[data-accordion-content]');
    const icon = accordion.querySelector('[data-accordion-icon]');
    const panel = getFilterAccordionPanel(content);
    
    if (!trigger || !content) return;
    
    // Skip if already initialized
    if (trigger.dataset.accordionInitialized) return;
    trigger.dataset.accordionInitialized = 'true';
    
    trigger.addEventListener('click', (e) => {
      e.preventDefault();
      const isExpanded = trigger.getAttribute('aria-expanded') === 'true';
      
      if (isExpanded) {
        // Close - measure current height first, then transition to 0
        const currentHeight = content.scrollHeight;
        content.style.height = currentHeight + 'px';
        
        // Force reflow
        content.offsetHeight;
        
        // Now transition to 0
        requestAnimationFrame(() => {
          trigger.setAttribute('aria-expanded', 'false');
          content.style.height = '0';
          content.classList.add('overflow-hidden');
          hideFilterAccordionPanel(panel);
          if (icon) icon.classList.remove('rotate-180');
        });
      } else {
        // Open - start at 0, then transition to full height
        content.classList.add('overflow-hidden');
        trigger.setAttribute('aria-expanded', 'true');
        hideFilterAccordionPanel(panel);
        
        // Force a layout recalculation to get accurate scrollHeight
        content.style.display = 'block';
        
        // Get the full height
        const fullHeight = content.scrollHeight;
        
        // Set to 0 first
        content.style.height = '0';
        content.style.display = '';
        
        // Force reflow
        content.offsetHeight;
        
        // Animate to full height
        requestAnimationFrame(() => {
          content.style.height = fullHeight + 'px';
          if (icon) icon.classList.add('rotate-180');
          showFilterAccordionPanel(panel);
        });
        
        // After transition, set to auto so it can resize naturally
        const transitionEndHandler = () => {
          content.style.height = 'auto';
          content.classList.remove('overflow-hidden');
          content.removeEventListener('transitionend', transitionEndHandler);
        };
        content.addEventListener('transitionend', transitionEndHandler);
      }
    });
  });
}

// Auto-initialize when module loads
initFilters();